import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import 'iconify-icon';
import { PRODUCTS } from '../data/products';

const Solutions = () => {
  const [activeTab, setActiveTab] = useState('individual');
  const { t } = useTranslation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <section id="solutions" className="relative py-24 border-t border-neutral-900" style={{ background: 'rgba(17, 17, 17, 0.78)' }}>
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* Section Header */}
        <div className="text-center mb-12">
          <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-4 block animate-on-scroll">
            {t('solutions.label')}
          </span>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6 animate-on-scroll delay-100 text-[#EAEAEA]">
            {t('solutions.title')}
          </h2>
        </div>

        {/* Tab Navigation */}
        <div className="flex justify-center mb-12 animate-on-scroll delay-300">
          <div className="bg-[#1A1A1A] border border-[#333333] p-1 rounded-full inline-flex">
            <button
              onClick={() => setActiveTab('individual')}
              className={`px-8 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                activeTab === 'individual'
                  ? 'bg-accent-orange text-white shadow-lg'
                  : 'text-neutral-400 hover:text-accent-orange'
              }`}
            >
              {t('solutions.tab_individual')}
            </button>
            <button
              onClick={() => setActiveTab('corporate')}
              className={`px-8 py-3 rounded-full text-sm font-medium transition-all duration-300 ${
                activeTab === 'corporate'
                  ? 'bg-accent-orange text-white shadow-lg'
                  : 'text-neutral-400 hover:text-accent-orange'
              }`}
            >
              {t('solutions.tab_corporate')}
            </button>
          </div>
        </div>

        {/* Solutions Grid — all 5 products in both tabs, content differs by audience */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {PRODUCTS.map((product, index) => {
            const tagline = activeTab === 'individual' ? product.short_individual : product.short_corporate;
            const description = activeTab === 'individual' ? product.long_individual : product.long_corporate;
            return (
              <Link
                key={product.id}
                to={`/${product.slug}`}
                data-testid={`solution-card-${product.id}`}
                className="glow-card hover-lift bg-[#1A1A1A] border border-[#333333] p-7 rounded-xl group cursor-pointer animate-on-scroll block hover:border-accent-orange transition-all duration-400"
                style={{ transitionDelay: `${index * 0.08}s` }}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = e.clientX - rect.left;
                  const y = e.clientY - rect.top;
                  e.currentTarget.style.setProperty('--x', `${x}px`);
                  e.currentTarget.style.setProperty('--y', `${y}px`);
                }}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-12 h-12 rounded-full bg-[#1F1F1F] border border-[#333333] flex items-center justify-center group-hover:scale-110 group-hover:border-accent-orange group-hover:bg-[#1F1208] transition-all duration-300 text-accent-orange">
                    <iconify-icon icon={product.icon} className="text-2xl"></iconify-icon>
                  </div>
                  <span className="text-[10px] font-mono tracking-widest text-accent-orange/70">
                    {activeTab === 'individual'
                      ? t('solutions.card_tag_individual')
                      : t('solutions.card_tag_corporate')}
                  </span>
                </div>
                <h3 className="text-xl md:text-2xl font-semibold mb-2 tracking-tight text-[#EAEAEA] group-hover:text-accent-orange transition-colors">
                  {product.name}
                </h3>
                <p className="text-sm text-neutral-300 mb-3 leading-[1.7] font-medium">
                  {tagline}
                </p>
                <p className="text-sm text-neutral-500 mb-5 leading-[1.7] line-clamp-4">
                  {description}
                </p>
                <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent-orange group-hover:translate-x-1 transition-transform">
                  Saiba mais
                  <iconify-icon icon="solar:arrow-right-linear"></iconify-icon>
                </span>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Solutions;